import React, { useEffect, useMemo, useRef } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Text,
  Textarea,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { CaretDown, CaretUp, Lock } from 'phosphor-react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { addDays, formatISO, startOfToday } from 'date-fns';
import { InputForm } from '../../../../../components/inputs/forms/InputForm';
import { CurrencyInputForm } from '../../../../../components/inputs/forms/CurrencyInputForm';
import { TextAreaInputForm } from '../../../../../components/inputs/forms/TextAreaInputForm';
import { IWizardFormValues, TIPO_ALTERACAO_OPTIONS, ehFluxoConstituinte } from '../schema';
import { CnaeSelector } from './CnaeSelector';
import { ScpConfigPanel } from './ScpConfigPanel';
import { EnderecoForm } from './EnderecoForm';
import { UfDropdown } from './UfDropdown';
import { UF_NOME_SELECT_OPTIONS } from '../../../../../constants/ufs';
import { CampoEmpresa, isCampoEditavel, tiposQueLiberam } from '../camposAlteracaoMap';
import { CNPJ_MASK } from '../../../../../utils/documentValidation';
import { cidadeDeAssinaturaComASede, IValoresAtuais, igualAoAtual } from '../derivarPatchesV3';

interface StepEmpresaProps {
  /**
   * Sessao 2026-05-11 (item 21 Fase 1): `numero_ultima_alteracao` extraido
   * do contrato vigente. Quando presente, o helper do campo
   * `numero_alteracao` ganha texto contextual.
   */
  numeroUltimaAlteracao?: number | null;
  /**
   * Sessao 2026-05-11 (item 18 Fase 2): `capital_social_atual` extraido
   * do contrato vigente. Quando presente em alteracao com `capital`
   * marcado, exibe "De: R$ X" acima do input do capital novo.
   */
  capitalSocialVigente?: number | null;
  /** Soma das quotas dos sócios do vigente (extração). Base da quantidade em alteração. */
  quotasTotalVigente?: number | null;
  /**
   * Issue #1158: os CNAEs foram SUGERIDOS por IA a partir do objeto social do
   * vigente (contrato sem código CNAE listado). Exibe aviso pro operador revisar
   * antes de gerar.
   */
  cnaesSugeridos?: boolean;
  /**
   * Valores ATUAIS do vigente. Com o tipo marcado, o campo e' o valor NOVO (nasce
   * vazio) e o atual aparece em leitura acima dele (varredura de 25/09).
   */
  atuais?: IValoresAtuais | null;
}

/** O valor atual do vigente, em leitura, acima do campo do valor novo. */
const ValorAtual = ({ valor }: { valor?: string | null }) =>
  valor ? (
    <Text fontSize="xs" color="gray.600" mb={1} noOfLines={3} title={valor}>
      Atual: <strong>{valor}</strong>
    </Text>
  ) : null;

/**
 * Sessao 2026-05-11 (item 2 Fase 2): wrapper que aplica estilos de
 * "VAI MUDAR" (borda verde + badge) ou "MANTIDO" (opacity reduzida +
 * cadeado + tooltip) ao redor de cada campo do form. Cor neutra quando
 * o campo nao e impactado pelo reflexo (constituicao, campo intrinseco
 * de alteracao, ou wildcard `outras`).
 */
type ReflexoIndicator = 'editavel' | 'mantido' | null;

const CampoReflexo: React.FC<{
  indicator: ReflexoIndicator;
  tooltipMantido?: string;
  children: React.ReactNode;
}> = ({ indicator, tooltipMantido, children }) => {
  if (indicator === null) return children as React.ReactElement;
  if (indicator === 'editavel') {
    return (
      <Box position="relative" pl={3} borderLeftWidth="3px" borderColor="green.400">
        <Badge
          colorScheme="green"
          fontSize="2xs"
          position="absolute"
          top="-2px"
          right={0}
          opacity={0.85}
        >
          vai mudar
        </Badge>
        {children}
      </Box>
    );
  }
  // mantido
  return (
    <Tooltip
      label={
        tooltipMantido ??
        'Mantido do contrato vigente. Para alterar, marque o tipo correspondente no Step 4.'
      }
      placement="top"
      hasArrow
      openDelay={300}
    >
      <Box position="relative" pl={3} borderLeftWidth="3px" borderColor="gray.300" opacity={0.7}>
        <Box position="absolute" top={1} right={1} color="gray.400">
          <Lock size={12} />
        </Box>
        {children}
      </Box>
    </Tooltip>
  );
};

// Da lista de opções do passo Tipos (o mapa à mão ficava para trás a cada tipo novo).
const TIPO_ALTERACAO_LABELS: Record<string, string> = Object.fromEntries(
  TIPO_ALTERACAO_OPTIONS.map(o => [o.value, o.label]),
);

const formatCurrencyBR = (valor: number | null | undefined): string => {
  if (valor == null) return '—';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(valor);
};

export const ENDERECO_VAZIO = {
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  uf: null,
  cep: '',
};

/**
 * Os três campos facultativos da filial, em branco. Vão em toda linha nova: campo indefinido faz
 * o RHF cair nos valores do rascunho retomado, e a filial nova herdava os dados de outra.
 */
export const DADOS_DA_FILIAL_VAZIOS = { atividades: '', capital_destacado: '', nome_fantasia: '' };

/**
 * Atividades, capital destacado e nome fantasia da filial: os três facultativos (DREI, item
 * 4.12.2). Na alteração, campo em branco = não muda.
 */
const DadosDaFilial = ({ base, alteracao = false }: { base: string; alteracao?: boolean }) => {
  const { setValue, watch } = useFormContext<IWizardFormValues>();
  const objeto = String(watch('objeto_social') ?? '').trim();
  const seMudar = alteracao ? 'Só se mudar.' : 'Opcional.';
  return (
    <Box mt={3}>
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
        <InputForm
          name={`${base}.nome_fantasia`}
          label="Nome fantasia da filial"
          controlMb={0}
          helper={`${seMudar} O nome empresarial da filial é sempre o da sociedade.`}
        />
        <Box>
          <CurrencyInputForm
            name={`${base}.capital_destacado`}
            label="Capital destacado"
            controlMb={0}
          />
          <Text fontSize="xs" color="gray.500" mt={1}>
            {seMudar} A soma dos destaques das filiais tem de ser menor que o capital social.
          </Text>
        </Box>
      </SimpleGrid>
      <Box mt={3}>
        <TextAreaInputForm
          name={`${base}.atividades`}
          label="Atividades da filial"
          rows={3}
          minH="90px"
          controlMb={0}
          placeholder="Ex.: comércio varejista de calçados; e reparação de calçados"
        />
        <Text fontSize="xs" color="gray.500" mt={1}>
          {seMudar} Sai como está, depois de &quot;atividades de:&quot;. Tem de reproduzir o objeto
          da sociedade, no todo ou em parte
          {alteracao ? ' — escreva a lista completa do que a filial passa a exercer' : ''}.{' '}
          <Button
            size="xs"
            variant="link"
            colorScheme="purple"
            isDisabled={!objeto}
            onClick={() =>
              setValue(`${base}.atividades` as any, objeto, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
          >
            Usar o objeto da sociedade
          </Button>
        </Text>
      </Box>
    </Box>
  );
};

/**
 * Issue #1158: preview READ-ONLY do objeto social (não há mais campo livre).
 * Em alteração alterando o objeto / constituição, mostra as descrições dos
 * CNAEs; quando mantido do vigente, mostra o texto preservado.
 */
const ObjetoSocialPreview: React.FC<{
  label: string;
  texto: string;
  vazioMsg?: string;
}> = ({ label, texto, vazioMsg }) => (
  <Box mt={3}>
    <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
      {label}
    </Text>
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" bg="gray.50" px={3} py={2}>
      <Text fontSize="sm" color={texto ? 'gray.800' : 'gray.400'}>
        {texto ||
          vazioMsg ||
          'Selecione ao menos um CNAE — o objeto social é montado automaticamente.'}
      </Text>
    </Box>
  </Box>
);

export const StepEmpresa: React.FC<StepEmpresaProps> = ({
  numeroUltimaAlteracao,
  capitalSocialVigente,
  quotasTotalVigente,
  cnaesSugeridos,
  atuais,
}) => {
  const { control, watch, setValue, getValues } = useFormContext<IWizardFormValues>();
  const isAlteracao = watch('tipo_operacao') === 'alteracao';
  // SCP (Fase 3): mostra o painel de config SCP e esconde porte (SCP não tem
  // enquadramento EPP/ME). Objeto/Junta relaxados no schema.
  const isScp = watch('natureza_societaria') === 'scp';
  // Briefing de legalização (imóvel/regime) abaixo é UI-only e ainda NÃO persiste (Fase 1
  // dá coluna + migration + whitelist — ver schema.ts:283). ESCONDIDO até lá pra não coletar
  // do operador dado que é descartado. Reativar na Fase 1: trocar por `true`.
  const mostrarBriefingLegalizacao = false;
  const tiposAlteracao = watch('tipos_alteracao') ?? [];
  // Alteração de CAPITAL (2026-07-15): mesma mecânica da constituição — o
  // operador informa Capital + Qtd de quotas e o Valor por quota é DERIVADO
  // (capital ÷ qtd), read-only, evitando input arbitrário/inconsistente.
  const capitalAlterado = isAlteracao && tiposAlteracao.includes('capital');
  const empresaIdValue = watch('empresa_id');
  // ── Item 6 (constituição): capital = qtd × valor, com VALOR DERIVADO ──
  // Operador informa Capital + Quantidade de quotas; o valor por quota é
  // calculado (capital ÷ qtd) e exibido read-only. Default R$ 1,00: a qtd
  // pré-preenche = capital. A qtd é editável (pluri usa valor variável); a
  // regra da uni (R$ 1,00) é checada no submit. Fase B libera valor na uni.
  const capitalSocialW = watch('capital_social');
  const qtdQuotasW = watch('capital_qtd_quotas');
  const capitalNum = Number(capitalSocialW);
  const qtdNum = Number(qtdQuotasW);
  const valorQuotaDerivado =
    Number.isFinite(capitalNum) && capitalNum > 0 && Number.isFinite(qtdNum) && qtdNum > 0
      ? Math.round((capitalNum / qtdNum) * 100) / 100
      : null;
  const capitalReconstruido =
    valorQuotaDerivado != null ? Math.round(qtdNum * valorQuotaDerivado * 100) / 100 : null;
  const capitalFecha =
    capitalReconstruido != null && Math.abs(capitalReconstruido - capitalNum) < 0.005;

  // Default "R$ 1,00 por quota": ao informar o capital com a qtd ainda vazia,
  // pré-preenche qtd = capital. Operador muda a qtd depois pra outro valor.
  useEffect(() => {
    if (isAlteracao && !capitalAlterado) return;
    if (capitalNum > 0 && (qtdQuotasW == null || (qtdQuotasW as unknown as string) === '')) {
      // Alteração parte da quantidade do vigente (o valor da quota sai derivado): assumir
      // R$ 1 por quota mudava o valor da quota sem ninguém pedir (varredura de 25/09).
      setValue(
        'capital_qtd_quotas',
        isAlteracao && quotasTotalVigente ? quotasTotalVigente : capitalNum,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capitalSocialW]);

  // Unipessoal (Fase A): valor variável (qtd ≠ capital) só é suportado no
  // pluripessoal; a uni exige R$ 1,00 (qtd = capital). Como a ordem agora é
  // Empresa → Sócios, o nº de sócios ainda não é conhecido aqui — então a qtd
  // fica livre e a regra da uni é validada no submit (handleGerar). Fase B
  // libera valor variável também na uni (back).

  // Propaga o valor derivado pro form (vai no payload; back valida
  // capital == qtd × valor no pluri; uni ignora e assume R$ 1,00).
  useEffect(() => {
    if (isAlteracao && !capitalAlterado) return;
    setValue('capital_valor_quota', valorQuotaDerivado);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valorQuotaDerivado]);

  // Sessao 2026-05-11 (item 17 Fase 1, fix bug ao digitar 1 trava):
  // empresa_id readonly **apenas** se o valor ja existia no mount do step
  // (i.e. veio do Step Verificacao ou da retomada via ?contratoId=).
  // Operador que entra no step sem empresa selecionada e digita um numero
  // NAO deve ficar bloqueado depois do primeiro digito.
  //
  // useRef captura o valor do primeiro render. Se for null no mount,
  // input fica livre pra sempre. Se for numero, fica locked ate o
  // operador clicar "Trocar empresa" (que limpa o ref tambem).
  const empresaIdInicialRef = useRef<number | null>(empresaIdValue ?? null);
  const isEmpresaIdLocked =
    empresaIdInicialRef.current != null && empresaIdValue === empresaIdInicialRef.current;
  const handleTrocarEmpresa = () => {
    setValue('empresa_id', null, { shouldDirty: true });
    empresaIdInicialRef.current = null;
  };

  // Sessao 2026-05-11 (item 19 Fase 1): bloco de CNAEs/objeto colapsavel.
  const cnaesRelevantes = !isAlteracao || tiposAlteracao.includes('objeto_social');
  const cnaesDisclosure = useDisclosure({ defaultIsOpen: cnaesRelevantes });

  // Issue #1158: o objeto social é DERIVADO dos CNAEs — não há mais campo livre.
  // Sincroniza `objeto_social` = descrições oficiais concatenadas por "; " quando
  // o objeto vem dos CNAEs (constituição LTDA/Holding OU alteração alterando o
  // objeto). SCP tem objeto próprio (ScpConfigPanel) — não toca. Quando o objeto
  // é MANTIDO do vigente, preserva o texto pré-preenchido (não sincroniza).
  const cnaesW = watch('cnaes');
  const objetoSocialDerivado = useMemo(
    () =>
      (cnaesW ?? [])
        .map(c => c.descricao?.trim() || c.codigo?.trim() || '')
        .filter(Boolean)
        .join('; '),
    [cnaesW],
  );
  // Alteração do objeto: o texto é do operador e vai pro contrato como está (Juliana, 21/09).
  const objetoVemDeCnaes = !isScp && ehFluxoConstituinte(watch('tipo_operacao'));
  const alteraObjeto = isAlteracao && tiposAlteracao.includes('objeto_social');

  const alteraSede = isAlteracao && tiposAlteracao.includes('endereco');
  // A cidade de assinatura segue a da sede nova (13/10). Só mudança feita com o passo aberto:
  // a retomada monta o passo já com os valores gravados.
  const cidadeDaSedeNova = watch('endereco_estruturado.cidade') ?? '';
  const cidadeDaSedeAnterior = useRef(cidadeDaSedeNova);
  useEffect(() => {
    const anterior = cidadeDaSedeAnterior.current;
    if (!alteraSede || cidadeDaSedeNova === anterior) return;
    cidadeDaSedeAnterior.current = cidadeDaSedeNova;
    const assinatura = cidadeDeAssinaturaComASede(
      getValues('cidade_assinatura'),
      cidadeDaSedeNova,
      anterior,
      atuais?.endereco,
      atuais?.cidade_assinatura,
    );
    if (assinatura != null) {
      setValue('cidade_assinatura', assinatura, { shouldDirty: true, shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cidadeDaSedeNova, alteraSede]);
  // Abertura de filial (Mylena, 21/09): um endereço por filial, pelo menos um.
  const alteraFilial = isAlteracao && tiposAlteracao.includes('abertura_filial');
  const filiaisArray = useFieldArray({ control, name: 'filiais' });
  useEffect(() => {
    // `getValues` e nao `fields`: no StrictMode o efeito roda 2x e o `fields`
    // da 2a passada ainda esta vazio — nasciam DUAS filiais em branco.
    if (alteraFilial && (getValues('filiais') ?? []).length === 0) {
      filiaisArray.append({ ...ENDERECO_VAZIO, ...DADOS_DA_FILIAL_VAZIOS });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alteraFilial]);
  // Extinção de filial (08/10): endereço, NIRE e CNPJ de cada filial encerrada.
  const encerraFilial = isAlteracao && tiposAlteracao.includes('extincao_filial');
  const fechadasArray = useFieldArray({ control, name: 'filiais_fechar' });
  useEffect(() => {
    if (encerraFilial && (getValues('filiais_fechar') ?? []).length === 0) {
      fechadasArray.append({ ...ENDERECO_VAZIO, nire: '', cnpj: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encerraFilial]);
  // Mudança de endereço de filial (09/10): quem é a filial (CNPJ, endereço de hoje) e o novo.
  const mudaFilial = isAlteracao && tiposAlteracao.includes('alteracao_filial');
  const alteradasArray = useFieldArray({ control, name: 'filiais_alterar' });
  const FILIAL_QUE_MUDA = {
    ...ENDERECO_VAZIO,
    nire: '',
    cnpj: '',
    novo: { ...ENDERECO_VAZIO },
    muda_endereco: true,
    ...DADOS_DA_FILIAL_VAZIOS,
  };
  useEffect(() => {
    if (mudaFilial && (getValues('filiais_alterar') ?? []).length === 0) {
      alteradasArray.append(FILIAL_QUE_MUDA);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mudaFilial]);
  useEffect(() => {
    if (!objetoVemDeCnaes) return;
    setValue('objeto_social', objetoSocialDerivado || null, {
      shouldValidate: true,
      shouldDirty: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objetoSocialDerivado, objetoVemDeCnaes]);

  // Sessao 2026-05-11 (item 20 Fase 1): razao_social_consolidada condicional.
  const mudaRazaoSocial = isAlteracao && tiposAlteracao.includes('razao_social');
  const mudaFantasia = isAlteracao && tiposAlteracao.includes('nome_fantasia');

  // 2026-07-02 (review #523): `razao_social_consolidada` é DERIVADO da razão
  // nova, sem campo visível. Antes havia 2 inputs de razão (o "novo" no topo + a
  // "consolidada") que confundiam e divergiam. O V3 nem usa a consolidada no
  // render (usa a razão extraída do vigente + o patch); é resquício V1/V2. Então
  // mantemos a coluna populada (DB/compat) espelhando a razão nova, sem o operador
  // ver/redigitar. Como não há input, não há override manual a perder no reload —
  // o problema de sobrescrita apontado no review vira inexistente por construção.
  const razaoSocialW = watch('razao_social');
  useEffect(() => {
    if (!mudaRazaoSocial) return;
    setValue('razao_social_consolidada', razaoSocialW ?? '', {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [razaoSocialW, mudaRazaoSocial, setValue]);

  // Sessao 2026-05-11 (item 2 Fase 2): sub-checkbox pra estender o reflexo
  // visual de `endereco` pra foro + cidade_assinatura. Default false — na
  // pratica acompanha, mas a decisao e do operador.
  const atualizarForoComEndereco = false;

  /**
   * Calcula o indicator visual de cada campo dado o estado atual.
   * `editavel` -> borda verde + badge "vai mudar"
   * `mantido` -> borda cinza + lock + readonly + tooltip
   * `null` -> sem indicator (constituicao ou campo intrinseco)
   */
  const camposState = useMemo(() => {
    const decide = (campo: CampoEmpresa): ReflexoIndicator => {
      if (!isAlteracao) return null;
      if (tiposAlteracao.includes('outras')) return null;
      const editavel = isCampoEditavel(campo, {
        tipoOperacao: 'alteracao',
        tiposAlteracao,
        atualizarForoComEndereco,
      });
      if (!editavel) return 'mantido';
      // So pinta "vai mudar" se HA tipo marcado que libera esse campo —
      // campos intrinsecos (cnpj, nire, etc) ficam neutros.
      const liberadores = tiposQueLiberam(campo);
      if (liberadores.some(t => tiposAlteracao.includes(t))) return 'editavel';
      // endereco com sub-checkbox extende foro/cidade — sao 'editavel'.
      if (
        atualizarForoComEndereco &&
        tiposAlteracao.includes('endereco') &&
        (campo === 'foro' || campo === 'cidade_assinatura')
      ) {
        return 'editavel';
      }
      return null;
    };
    const campos: CampoEmpresa[] = [
      'razao_social',
      'nome_fantasia',
      'cnpj',
      'capital_social',
      'objeto_social',
      'cnaes',
      'endereco',
      'foro',
      'cidade_assinatura',
      'data_inicio_atividades',
      'junta_comercial_estado',
      'junta_comercial_sigla',
      'nire',
      // 'data_constituicao' saiu (2026-08-06): nao e' mais renderizado em alteracao.
      'numero_alteracao',
      'razao_social_consolidada',
    ];
    const obj: Record<CampoEmpresa, ReflexoIndicator> = {} as never;
    campos.forEach(c => {
      obj[c] = decide(c);
    });
    return obj;
  }, [isAlteracao, tiposAlteracao, atualizarForoComEndereco]);

  // Porte "Demais" (não ME/EPP) leva o visto do advogado no contrato (Juliana, 21/09).
  const campoPorte = !isScp && (
    <Controller
      control={control}
      name="porte"
      render={({ field, fieldState }) => (
        <FormControl isInvalid={!!fieldState.error} isRequired={!isAlteracao} mb={0}>
          <FormLabel fontWeight="bold" mb={1} htmlFor="porte">
            Porte empresarial{isAlteracao ? ' (opcional)' : ''}
          </FormLabel>
          <Select
            id="porte"
            placeholder={isAlteracao ? 'Não informado' : 'Selecione o porte'}
            {...field}
            value={field.value ?? ''}
            // A option vazia ("Não informado") grava nulo, não '': o yup só aceita nulo.
            onChange={e => field.onChange(e.target.value || null)}
          >
            <option value="EPP">EPP — Empresa de Pequeno Porte</option>
            <option value="ME">ME — Microempresa</option>
            <option value="nenhum">Demais — nem ME nem EPP (leva visto do advogado)</option>
          </Select>
          {isAlteracao && !field.value && (
            <Text fontSize="xs" color="gray.600" mt={1}>
              Sem o porte, o contrato sai sem o visto do advogado (exigido fora de ME/EPP) e a
              vistoria pede para conferir.
            </Text>
          )}
          {fieldState.error?.message && (
            <FormErrorMessage>{fieldState.error.message}</FormErrorMessage>
          )}
        </FormControl>
      )}
    />
  );

  const tooltipMantidoParaCampo = (campo: CampoEmpresa): string => {
    const liberadores = tiposQueLiberam(campo);
    if (liberadores.length === 0) {
      return 'Mantido do contrato vigente. Marque "Outras" no Step 4 pra liberar.';
    }
    const labels = liberadores.map(t => TIPO_ALTERACAO_LABELS[t]).join(', ');
    return `Mantido do contrato vigente. Para alterar, marque "${labels}" no Step 4.`;
  };

  // Helper pra simplificar JSX: gera props prontas (isReadOnly etc) pra cada campo.
  const campoProps = (campo: CampoEmpresa) => {
    const ind = camposState[campo];
    return {
      isReadOnly: ind === 'mantido',
      reflexoIndicator: ind,
      tooltipMantido: tooltipMantidoParaCampo(campo),
    };
  };

  // Bounds das datas — iguais ao schema. Recalculados no render (cheap).
  // `todayIso` saiu junto com o campo `data_constituicao` (2026-08-06).
  const today30Iso = formatISO(addDays(startOfToday(), 30), {
    representation: 'date',
  });
  const minDateIso = '1900-01-01';

  return (
    <Box>
      <Text fontSize="lg" fontWeight="bold" mb={4}>
        Dados da empresa
      </Text>

      <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
        {/* ID da empresa (vínculo ao cadastro) e CNPJ só fazem sentido em
            alteração — constituição é uma sociedade NOVA (sem cadastro/CNPJ). */}
        {isAlteracao && (
          <Box>
            <InputForm
              name="empresa_id"
              label="ID da empresa (interno)"
              type="number"
              controlMb={0}
              isReadOnly={isEmpresaIdLocked}
              helper={
                isEmpresaIdLocked
                  ? 'Empresa selecionada na verificação inicial.'
                  : 'Opcional — vincula ao cadastro de empresa existente'
              }
            />
            {isEmpresaIdLocked && (
              <Button
                size="xs"
                variant="link"
                colorScheme="blue"
                mt={1}
                onClick={handleTrocarEmpresa}
              >
                Trocar empresa
              </Button>
            )}
          </Box>
        )}
        <Box>
          {mudaRazaoSocial && <ValorAtual valor={atuais?.razao_social} />}
          <CampoReflexo
            indicator={campoProps('razao_social').reflexoIndicator}
            tooltipMantido={campoProps('razao_social').tooltipMantido}
          >
            <InputForm
              name="razao_social"
              label={mudaRazaoSocial ? 'Nova razão social' : 'Razão social'}
              controlMb={0}
              isReadOnly={campoProps('razao_social').isReadOnly}
            />
          </CampoReflexo>
        </Box>
        <Box>
          {mudaFantasia && <ValorAtual valor={atuais?.nome_fantasia} />}
          <CampoReflexo
            indicator={campoProps('nome_fantasia').reflexoIndicator}
            tooltipMantido={campoProps('nome_fantasia').tooltipMantido}
          >
            <InputForm
              name="nome_fantasia"
              label={mudaFantasia ? 'Novo nome fantasia' : 'Nome fantasia'}
              controlMb={0}
              isReadOnly={campoProps('nome_fantasia').isReadOnly}
            />
          </CampoReflexo>
        </Box>
        {isAlteracao && (
          <InputForm name="cnpj" label="CNPJ" mask={CNPJ_MASK} controlMb={0} isRequired />
        )}
        <Box>
          {/* Sessao 2026-05-11 (item 18 Fase 2): em alteracao com `capital`
              marcado, exibe valor anterior extraido do vigente. */}
          {isAlteracao && tiposAlteracao.includes('capital') && capitalSocialVigente != null && (
            <Text fontSize="xs" color="gray.600" mb={1}>
              De: <strong>{formatCurrencyBR(capitalSocialVigente)}</strong>
              {quotasTotalVigente
                ? ` em ${quotasTotalVigente.toLocaleString('pt-BR')} quotas de ${formatCurrencyBR(
                    Math.round((capitalSocialVigente / quotasTotalVigente) * 100) / 100,
                  )}`
                : ''}{' '}
              (atual no contrato vigente)
            </Text>
          )}
          <CampoReflexo
            indicator={campoProps('capital_social').reflexoIndicator}
            tooltipMantido={campoProps('capital_social').tooltipMantido}
          >
            <InputForm
              name="capital_social"
              label={
                isAlteracao && tiposAlteracao.includes('capital')
                  ? 'Capital social novo (R$)'
                  : 'Capital social (R$)'
              }
              type="number"
              controlMb={0}
              isReadOnly={campoProps('capital_social').isReadOnly}
              helper={
                isAlteracao
                  ? 'Valor total da sociedade.'
                  : 'Valor total. O valor por quota = capital ÷ quantidade de quotas (abaixo).'
              }
            />
          </CampoReflexo>
        </Box>
        <InputForm
          name="data_inicio_atividades"
          label="Data de início das atividades"
          type="date"
          min={minDateIso}
          max={today30Iso}
          controlMb={0}
          isRequired={watch('tipo_operacao') === 'constituicao' && !isScp}
          helper={
            watch('tipo_operacao') === 'constituicao'
              ? 'Vai no contrato em número e por extenso (cl. 4ª). Até 30 dias no futuro.'
              : 'Pode ser até 30 dias no futuro (constituição em curso)'
          }
        />
        {/* Bloco 13 v3 alteração — quando `capital` está marcado, expõe
            qtd_quotas + valor_quota pra que o operador decida COMO o
            capital muda: emissão de novas quotas (qtd cresce, valor
            unitário mantém) ou aumento do valor unitário (qtd mantém,
            valor cresce). Sem isso o sistema cai num estado matemático
            inconsistente (capital_total ÷ qtd_quotas ≠ valor_quota). */}
        {isAlteracao && tiposAlteracao.includes('capital') && (
          <Box
            gridColumn={{ md: 'span 2' }}
            mt={2}
            p={3}
            borderWidth="1px"
            borderRadius="md"
            borderColor="green.200"
            bg="green.50"
          >
            <Text fontSize="xs" fontWeight="bold" color="green.700" mb={2}>
              Como a alteração de capital se realiza
            </Text>
            <Text fontSize="2xs" color="gray.700" mb={3}>
              Informe o Capital total e a Quantidade de quotas. O Valor por quota é calculado
              automaticamente (capital ÷ qtd). Emissão de novas quotas: aumente a quantidade (valor
              por quota mantém). Aumento do valor unitário: mantenha a quantidade (valor por quota
              sobe).
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
              <InputForm
                name="capital_qtd_quotas"
                label="Quantidade total de quotas"
                type="number"
                controlMb={0}
                helper="Soma das quotas dos sócios após a alteração"
              />
              <InputForm
                name="capital_valor_quota"
                label="Valor por quota (R$)"
                type="number"
                controlMb={0}
                isReadOnly
                helper="Calculado automaticamente: capital ÷ quantidade de quotas."
              />
            </SimpleGrid>
          </Box>
        )}
        {/* `foro` e `endereco` em texto são da alteração (o vigente, em leitura). Na
            constituição valem o EnderecoForm abaixo e `foro_cidade`/`foro_uf`. */}
        {isAlteracao && (
          <CampoReflexo
            indicator={campoProps('foro').reflexoIndicator}
            tooltipMantido={campoProps('foro').tooltipMantido}
          >
            <InputForm
              name="foro"
              label="Foro"
              placeholder="Comarca / cidade"
              controlMb={0}
              isRequired
              isReadOnly={campoProps('foro').isReadOnly}
            />
          </CampoReflexo>
        )}
        {/* Sede nova (08/10): os 7 campos, com busca de CEP, no lugar do texto livre que
            o front quebrava por heurística. */}
        {alteraSede && (
          <Box gridColumn={{ md: 'span 2' }}>
            <ValorAtual valor={atuais?.endereco} />
            <CampoReflexo indicator={campoProps('endereco').reflexoIndicator}>
              <EnderecoForm
                basePath="endereco_estruturado"
                label="Novo endereço da sede"
                isRequired
                numeroOpcional
              />
            </CampoReflexo>
          </Box>
        )}
        {isAlteracao && !alteraSede && (
          <Box>
            <CampoReflexo
              indicator={campoProps('endereco').reflexoIndicator}
              tooltipMantido={campoProps('endereco').tooltipMantido}
            >
              <InputForm
                name="endereco"
                label="Endereço completo da sede"
                controlMb={0}
                isReadOnly={campoProps('endereco').isReadOnly}
              />
            </CampoReflexo>
            {/* O sub-checkbox "Atualizar também foro e cidade" saiu (25/09): nao ia
                para o payload e prometia um efeito que nao existia. O motor novo leva o
                foro junto com a sede. */}
          </Box>
        )}
        <CampoReflexo
          indicator={campoProps('cidade_assinatura').reflexoIndicator}
          tooltipMantido={campoProps('cidade_assinatura').tooltipMantido}
        >
          <InputForm
            name="cidade_assinatura"
            label="Cidade de assinatura"
            controlMb={0}
            helper="Geralmente a cidade da sede"
            isRequired
            isReadOnly={campoProps('cidade_assinatura').isReadOnly}
          />
        </CampoReflexo>
        {/* Sem este campo toda alteracao saia com "____ de ____" no local e data:
            o back monta a data so' da coluna data_assinatura (varredura de 25/09). */}
        {isAlteracao && (
          <InputForm
            name="data_assinatura"
            label="Data de assinatura da alteração"
            type="date"
            controlMb={0}
            helper="Vazio: o documento sai com a data em branco, para preencher à mão."
          />
        )}
        {/* Em alteracao (v2): dropdown com nome extenso ("São Paulo")
            grava em `junta_comercial_estado`. Em constituicao (v3): adapter
            `_resolver_junta_uf` precisa SIGLA (2 letras) em
            `junta_comercial_sigla` — dropdown nome extenso ficaria inutil
            pro path v3. UfDropdown abaixo escreve sigla direta. */}
        {isAlteracao && (
          <Controller
            control={control}
            name="junta_comercial_estado"
            render={({ field, fieldState }) => (
              <FormControl isInvalid={!!fieldState.error} mb={0}>
                <FormLabel fontWeight="bold" mb={1} mr={3} htmlFor="junta_comercial_estado">
                  Estado da Junta Comercial
                </FormLabel>
                <Select
                  id="junta_comercial_estado"
                  placeholder="Selecione o estado"
                  {...field}
                  value={field.value ?? ''}
                >
                  {UF_NOME_SELECT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
                {fieldState.error?.message && (
                  <FormErrorMessage>{fieldState.error.message}</FormErrorMessage>
                )}
              </FormControl>
            )}
          />
        )}
        {/* SCP não registra na Junta (art. 993) → sem UF da Junta. */}
        {!isAlteracao && !isScp && (
          <UfDropdown
            name="junta_comercial_sigla"
            label="UF da Junta Comercial"
            isRequired
            controlMb={0}
          />
        )}
      </SimpleGrid>

      {/* Natureza no TOPO (antes do objeto) — o operador escolhe a natureza
          ANTES de preencher objeto/CNAE, evitando trabalho perdido ao trocar
          para SCP (que usa objeto próprio). */}
      {!isAlteracao && (
        <Box mt={4} maxW={{ md: '50%' }}>
          <Controller
            control={control}
            name="natureza_societaria"
            render={({ field, fieldState }) => (
              <FormControl isInvalid={!!fieldState.error} mb={0}>
                <FormLabel fontWeight="bold" mb={1} htmlFor="natureza_societaria">
                  Natureza societária
                </FormLabel>
                <Select id="natureza_societaria" {...field} value={field.value ?? 'ltda'}>
                  <option value="ltda">LTDA — Sociedade Limitada</option>
                  <option value="holding">Holding — administração de bens/participações</option>
                  <option value="scp">SCP — Sociedade em Conta de Participação</option>
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Escolha primeiro a natureza — ela define o objeto abaixo. LTDA/Holding usam CNAEs;
                  SCP abre a configuração própria (papel ostensiva/participante, sem Junta,
                  objeto/capital/prazo próprios).
                </Text>
                {fieldState.error?.message && (
                  <FormErrorMessage>{fieldState.error.message}</FormErrorMessage>
                )}
              </FormControl>
            )}
          />
        </Box>
      )}

      {/* Texto pronto do jurídico (15/10): o texto vem do back, com a concordância dos sócios. A
          regência supletiva os dois modelos já trazem. */}
      {watch('tipo_operacao') === 'constituicao' && !isScp && (
        <Box mt={4}>
          <Controller
            control={control}
            name="textos_prontos"
            render={({ field }) => (
              <FormControl mb={0}>
                <FormLabel fontWeight="bold" mb={1}>
                  Textos prontos
                </FormLabel>
                <Checkbox
                  isChecked={(field.value ?? []).includes('distribuicao_de_lucros')}
                  onChange={e => field.onChange(e.target.checked ? ['distribuicao_de_lucros'] : [])}
                >
                  Distribuição de lucros (texto do escritório)
                </Checkbox>
              </FormControl>
            )}
          />
        </Box>
      )}

      {/* SCP: o objeto (livre×CNAE) é tratado no ScpConfigPanel — esconde o
          bloco CNAE/objeto padrão pra não duplicar. */}
      <Box mt={4} display={isScp ? 'none' : undefined}>
        {isAlteracao && !cnaesRelevantes ? (
          <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
            <Flex
              align="center"
              justify="space-between"
              cursor="pointer"
              onClick={cnaesDisclosure.onToggle}
            >
              <Box>
                <Text fontSize="sm" fontWeight="semibold">
                  CNAEs e objeto social
                </Text>
                <Text fontSize="xs" color="gray.500">
                  Mantidos do contrato vigente. Para mudar, marque &ldquo;Objeto social&rdquo; no
                  passo anterior.
                </Text>
              </Box>
              {cnaesDisclosure.isOpen ? (
                <CaretUp size={16} color="#718096" />
              ) : (
                <CaretDown size={16} color="#718096" />
              )}
            </Flex>
            <Collapse in={cnaesDisclosure.isOpen} animateOpacity>
              <Box mt={3}>
                {/* So' leitura sem o tipo marcado: editar aqui ia so' para o cadastro,
                    nunca para o documento, e gravava CNAE que ninguem pediu (inclusive a
                    sugestao da IA) — varredura de 25/09. */}
                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                  CNAEs ({(watch('cnaes') ?? []).length})
                </Text>
                {(watch('cnaes') ?? []).map(c => (
                  <Text key={`${c.codigo}-${c.descricao}`} fontSize="sm">
                    {c.codigo} — {c.descricao}
                  </Text>
                ))}
                <ObjetoSocialPreview
                  label="Objeto social (mantido do vigente)"
                  texto={watch('objeto_social') ?? ''}
                  vazioMsg="Objeto social preservado do contrato vigente."
                />
              </Box>
            </Collapse>
          </Box>
        ) : (
          <CampoReflexo
            indicator={isAlteracao ? campoProps('objeto_social').reflexoIndicator : null}
            tooltipMantido={campoProps('objeto_social').tooltipMantido}
          >
            {cnaesSugeridos && (
              <Box
                mb={3}
                borderWidth="1px"
                borderColor="blue.200"
                bg="blue.50"
                borderRadius="md"
                px={3}
                py={2}
              >
                <Text fontSize="xs" color="blue.800">
                  <b>CNAEs sugeridos por IA</b> a partir do objeto social do contrato vigente (que
                  não listava códigos). Revise e ajuste antes de gerar.
                </Text>
              </Box>
            )}
            {alteraObjeto ? (
              <>
                <ValorAtual valor={atuais?.objeto_social} />
                <Controller
                  control={control}
                  name="objeto_social"
                  render={({ field, fieldState }) => (
                    <FormControl isInvalid={!!fieldState.error} isRequired mb={3}>
                      <FormLabel fontWeight="bold" mb={1} htmlFor="objeto_social">
                        Novo objeto social (texto do contrato)
                      </FormLabel>
                      <Textarea
                        id="objeto_social"
                        rows={4}
                        placeholder="Ex.: Atividades de sonorização e de iluminação. Produção musical. Promoção de vendas."
                        {...field}
                        value={field.value ?? ''}
                      />
                      <FormHelperText fontSize="xs">
                        Sai como está: &quot;Os sócios resolvem alterar o objeto social da sociedade
                        para: &lt;texto&gt;&quot;, seguido de &quot;E exercerá as seguintes
                        atividades econômicas:&quot; com os CNAEs abaixo.
                      </FormHelperText>
                      {igualAoAtual(field.value ?? '', atuais?.objeto_social) && (
                        <Text fontSize="xs" color="orange.600" mt={1}>
                          O texto é igual ao objeto atual e vai assim para o contrato. Se a
                          alteração acrescenta atividades, descreva-as também no texto.
                        </Text>
                      )}
                      {fieldState.error?.message && (
                        <FormErrorMessage>{fieldState.error.message}</FormErrorMessage>
                      )}
                    </FormControl>
                  )}
                />
                <CnaeSelector textoObjeto={watch('objeto_social') ?? ''} />
              </>
            ) : (
              <>
                <CnaeSelector />
                <ObjetoSocialPreview
                  label="Objeto social (gerado a partir dos CNAEs)"
                  texto={objetoSocialDerivado}
                />
              </>
            )}
          </CampoReflexo>
        )}
      </Box>

      {alteraFilial && (
        <Box mt={6} pt={6} borderTopWidth="1px" borderColor="gray.200">
          <Text fontSize="sm" fontWeight="bold" mb={1} color="purple.600">
            Filiais abertas por este ato
          </Text>
          <Text fontSize="xs" color="gray.500" mb={3}>
            Cada filial vira uma frase da cláusula &quot;Da abertura de filial&quot; e um parágrafo
            na cláusula da sede, na consolidação. Nome fantasia, capital destacado e atividades são
            opcionais: preenchidos, entram na frase e no parágrafo.
          </Text>
          {filiaisArray.fields.map((filial, indice) => (
            <Box key={filial.id} mb={4}>
              <EnderecoForm
                basePath={`filiais.${indice}`}
                label={`Filial ${indice + 1}`}
                isRequired
                numeroOpcional
              />
              <DadosDaFilial base={`filiais.${indice}`} />
              {filiaisArray.fields.length > 1 && (
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  mt={1}
                  onClick={() => filiaisArray.remove(indice)}
                >
                  Remover filial {indice + 1}
                </Button>
              )}
            </Box>
          ))}
          <Button
            size="sm"
            variant="outline"
            onClick={() => filiaisArray.append({ ...ENDERECO_VAZIO, ...DADOS_DA_FILIAL_VAZIOS })}
          >
            Adicionar filial
          </Button>
        </Box>
      )}

      {encerraFilial && (
        <Box mt={6} pt={6} borderTopWidth="1px" borderColor="gray.200">
          <Text fontSize="sm" fontWeight="bold" mb={1} color="purple.600">
            Filiais encerradas por este ato
          </Text>
          <Text fontSize="xs" color="gray.500" mb={3}>
            Endereço, NIRE e CNPJ da filial, iguais aos da Junta: vão para a cláusula &quot;Da
            extinção de filial&quot;. Ao gerar, o sistema retira da consolidação o que só trata da
            filial encerrada; a vistoria lista o que saiu ou avisa se ficou para você retirar.
          </Text>
          {fechadasArray.fields.map((filial, indice) => (
            <Box key={filial.id} mb={4}>
              <EnderecoForm
                basePath={`filiais_fechar.${indice}`}
                label={`Filial ${indice + 1}`}
                isRequired
                numeroOpcional
                cepSoCompleta
              />
              <SimpleGrid columns={{ base: 1, md: 2 }} gap={3} mt={3}>
                <InputForm
                  name={`filiais_fechar.${indice}.nire`}
                  label="NIRE da filial"
                  maxLength={14}
                  controlMb={0}
                  isRequired
                  helper="11 dígitos, como na certidão da Junta"
                />
                <InputForm
                  name={`filiais_fechar.${indice}.cnpj`}
                  label="CNPJ da filial"
                  mask={CNPJ_MASK}
                  controlMb={0}
                  isRequired
                />
              </SimpleGrid>
              {fechadasArray.fields.length > 1 && (
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  mt={1}
                  onClick={() => fechadasArray.remove(indice)}
                >
                  Remover filial {indice + 1}
                </Button>
              )}
            </Box>
          ))}
          <Button
            size="sm"
            variant="outline"
            onClick={() => fechadasArray.append({ ...ENDERECO_VAZIO, nire: '', cnpj: '' })}
          >
            Adicionar filial
          </Button>
        </Box>
      )}

      {mudaFilial && (
        <Box mt={6} pt={6} borderTopWidth="1px" borderColor="gray.200">
          <Text fontSize="sm" fontWeight="bold" mb={1} color="purple.600">
            Filiais alteradas por este ato
          </Text>
          <Text fontSize="xs" color="gray.500" mb={3}>
            CNPJ e endereço de hoje identificam a filial (iguais aos da Junta). Informe o que muda:
            o endereço, o nome fantasia, o capital destacado ou as atividades. Ao gerar, o sistema
            ajusta a consolidação; a vistoria diz o que fez ou avisa se ficou para você ajustar.
          </Text>
          {alteradasArray.fields.map((filial, indice) => (
            <Box key={filial.id} mb={5}>
              <SimpleGrid columns={{ base: 1, md: 2 }} gap={3} mb={3}>
                <InputForm
                  name={`filiais_alterar.${indice}.cnpj`}
                  label={`CNPJ da filial ${indice + 1}`}
                  mask={CNPJ_MASK}
                  controlMb={0}
                  isRequired
                />
                <InputForm
                  name={`filiais_alterar.${indice}.nire`}
                  label="NIRE da filial"
                  maxLength={14}
                  controlMb={0}
                  helper="Opcional. 11 dígitos, como na certidão da Junta"
                />
              </SimpleGrid>
              <EnderecoForm
                basePath={`filiais_alterar.${indice}`}
                label="Endereço de hoje"
                isRequired
                numeroOpcional
                cepSoCompleta
              />
              <Controller
                control={control}
                name={`filiais_alterar.${indice}.muda_endereco` as const}
                render={({ field }) => (
                  <Checkbox
                    mt={3}
                    isChecked={field.value !== false}
                    onChange={e => field.onChange(e.target.checked)}
                  >
                    <Text fontSize="sm">A filial muda de endereço</Text>
                  </Checkbox>
                )}
              />
              {watch(`filiais_alterar.${indice}.muda_endereco`) !== false && (
                <Box mt={3}>
                  <EnderecoForm
                    basePath={`filiais_alterar.${indice}.novo`}
                    label="Endereço novo"
                    isRequired
                    numeroOpcional
                  />
                </Box>
              )}
              <DadosDaFilial base={`filiais_alterar.${indice}`} alteracao />
              {alteradasArray.fields.length > 1 && (
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  mt={1}
                  onClick={() => alteradasArray.remove(indice)}
                >
                  Remover filial {indice + 1}
                </Button>
              )}
            </Box>
          ))}
          <Button
            size="sm"
            variant="outline"
            onClick={() => alteradasArray.append(FILIAL_QUE_MUDA)}
          >
            Adicionar filial
          </Button>
        </Box>
      )}

      {isAlteracao && (
        <>
          <Divider my={6} />
          <Text fontSize="md" fontWeight="bold" mb={3} color="purple.600">
            Dados da alteração contratual
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
            <InputForm
              name="numero_alteracao"
              label="Número da alteração"
              type="number"
              isRequired
              controlMb={0}
              helper={
                numeroUltimaAlteracao != null && numeroUltimaAlteracao > 0
                  ? `Próximo após a ${numeroUltimaAlteracao}ª alteração registrada no contrato vigente.`
                  : '1 para a 1ª alteração, 2 para a 2ª, etc. O ordinal (1ª, 2ª) é gerado'
              }
            />
            {campoPorte}
            {/* Sessao 2026-08-06 (Juliana): "Data da constituicao original" SAIU da
                alteracao. O motor v3 de alteracao nunca le `data_constituicao` — os
                unicos consumidores sao a TRANSFORMACAO (StepEmpresarioIndividual, onde
                e' a data de arquivamento do EI) e os engines v1/v2, mortos pra alteracao.
                Campo pedia dado que nao entra em documento nenhum. */}
            <InputForm
              name="junta_comercial_sigla"
              label="Sigla da Junta Comercial"
              controlMb={0}
              placeholder="JUCESP"
              helper="Ex: JUCESP, JUCEG, JUCERJA, JUCEMG"
            />
            <InputForm
              name="nire"
              label="NIRE (opcional)"
              // DREI nao exige mais NIRE como pre-requisito de alteracao —
              // campo agora e opcional. Quando preenchido, valida 11 (JUCEG)
              // ou 12 digitos (demais juntas) via NIRE_REGEX (schema.ts).
              // Mask removida (react-input-mask 2.x nao suporta `?` opcional).
              maxLength={12}
              controlMb={0}
              helper="11 ou 12 dígitos (sem traço/ponto). Pode deixar em branco."
            />
            {/* `razao_social_consolidada` NÃO tem mais campo próprio: o V3 usa a
                razão extraída do vigente (antigo) + o patch de razão (novo), e o
                campo consolidada era resquício V1/V2 que confundia (2 campos de
                razão). Agora é DERIVADO da razão nova pelo useEffect acima —
                populado no form/payload/DB sem input visível. Review #523. */}
          </SimpleGrid>
        </>
      )}

      {/* ============================================================
          Schema-extension v3 (elo 3) — campos estruturados pra
          constituicao. Render so em !isAlteracao (alteracao continua
          em v2 com campos legacy `endereco` textarea + `foro` string).

          Backend (elo 2 + adapter elo 4) usa esses campos via
          `_resolver_foro_cidade_uf` (prioriza foro_cidade/foro_uf sobre
          legacy `foro`) e `EnderecoForm` desempacotado pros 7 flat M3.
      ============================================================ */}
      {!isAlteracao && (
        <Box mt={6} pt={6} borderTopWidth="1px" borderColor="gray.200">
          <Text fontSize="sm" fontWeight="bold" mb={3} color="blue.600">
            Dados estruturados (constituição)
          </Text>

          <EnderecoForm basePath="endereco_estruturado" label="Sede da empresa" isRequired />

          <SimpleGrid columns={{ base: 1, md: 12 }} gap={3} mt={4}>
            <Box gridColumn={{ md: 'span 8' }}>
              <InputForm
                name="foro_cidade"
                label="Foro — cidade"
                isRequired
                controlMb={0}
                helper="Comarca onde controvérsias contratuais serão dirimidas"
              />
            </Box>
            <Box gridColumn={{ md: 'span 4' }}>
              <UfDropdown name="foro_uf" label="Foro — UF" isRequired controlMb={0} />
            </Box>
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, md: 2 }} gap={3} mt={4}>
            <InputForm
              name="capital_qtd_quotas"
              label="Quantidade total de quotas"
              type="number"
              isRequired
              controlMb={0}
              helper="Os sócios vão distribuir esta quantidade. Deixe igual ao capital para quotas de R$ 1,00."
            />
            <FormControl mb={0}>
              <FormLabel fontWeight="bold" mb={1}>
                Valor por quota (R$)
              </FormLabel>
              <Input
                value={valorQuotaDerivado != null ? formatCurrencyBR(valorQuotaDerivado) : '—'}
                isReadOnly
                bg="gray.50"
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Derivado: capital ÷ quantidade de quotas.
              </Text>
            </FormControl>
          </SimpleGrid>

          {/* Confirmação visual: capital = qtd × valor por quota (item 6).
              Verde quando fecha; vermelho na sobra de centavo (capital não
              divisível pela qtd) — o back recusaria com 422. */}
          {valorQuotaDerivado != null && (
            <Box
              mt={3}
              p={3}
              borderWidth="1px"
              borderRadius="md"
              borderColor={capitalFecha ? 'green.200' : 'red.200'}
              bg={capitalFecha ? 'green.50' : 'red.50'}
            >
              <Text fontSize="sm" color={capitalFecha ? 'green.700' : 'red.700'}>
                {capitalFecha ? (
                  <>
                    ✓ {formatCurrencyBR(capitalNum)} = {qtdNum} {qtdNum === 1 ? 'quota' : 'quotas'}{' '}
                    × {formatCurrencyBR(valorQuotaDerivado)}
                  </>
                ) : (
                  <>
                    ✗ Não fecha: {qtdNum} × {formatCurrencyBR(valorQuotaDerivado)} ={' '}
                    {formatCurrencyBR(capitalReconstruido ?? 0)} ≠ {formatCurrencyBR(capitalNum)}.
                    Ajuste a quantidade de quotas para um divisor exato do capital.
                  </>
                )}
              </Text>
            </Box>
          )}

          <SimpleGrid columns={{ base: 1, md: 2 }} gap={3} mt={4}>
            {campoPorte}
            <InputForm
              name="data_assinatura"
              label="Data de assinatura do contrato"
              type="date"
              controlMb={0}
              helper="Quando o contrato será assinado. Vazio = hoje na geração. NÃO confundir com 'data de início das atividades' acima."
            />
          </SimpleGrid>

          {/* ── Fase 0 (legalização) — levantamento do imóvel + regime. UI-only:
              coletado pra o processo na junta/prefeitura (viabilidade), NÃO vai pro
              contrato nem persiste ainda (Fase 1 dá coluna + migration). ESCONDIDO
              (mostrarBriefingLegalizacao=false) até a Fase 1 conectar a persistência. ── */}
          {mostrarBriefingLegalizacao && !isScp && (
            <Box mt={6} pt={5} borderTopWidth="1px" borderColor="gray.200">
              <Text fontSize="sm" fontWeight="bold" mb={1} color="teal.600">
                Levantamento para a legalização (imóvel e regime)
              </Text>
              <Text fontSize="xs" color="gray.500" mb={3}>
                Usado no processo na junta/prefeitura (viabilidade) — ainda não entra no contrato.
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                <Controller
                  control={control}
                  name="imovel_tipo"
                  render={({ field }) => (
                    <FormControl mb={0}>
                      <FormLabel fontWeight="bold" mb={1}>
                        Imóvel da sede
                      </FormLabel>
                      <Select placeholder="Selecione" {...field} value={field.value ?? ''}>
                        <option value="proprio">Próprio</option>
                        <option value="alugado">Alugado</option>
                      </Select>
                    </FormControl>
                  )}
                />
                <Controller
                  control={control}
                  name="imovel_zona"
                  render={({ field }) => (
                    <FormControl mb={0}>
                      <FormLabel fontWeight="bold" mb={1}>
                        Zona
                      </FormLabel>
                      <Select placeholder="Selecione" {...field} value={field.value ?? ''}>
                        <option value="urbana">Urbana</option>
                        <option value="rural">Rural</option>
                      </Select>
                    </FormControl>
                  )}
                />
                <InputForm
                  name="imovel_area_total"
                  label="Área total (m²)"
                  type="number"
                  controlMb={0}
                />
                <InputForm
                  name="imovel_area_ocupada"
                  label="Área ocupada (m²)"
                  type="number"
                  controlMb={0}
                />
                <InputForm
                  name="imovel_inscricao_iptu"
                  label="Inscrição imobiliária (IPTU)"
                  controlMb={0}
                />
                <Controller
                  control={control}
                  name="regime_tributario_pretendido"
                  render={({ field }) => (
                    <FormControl mb={0}>
                      <FormLabel fontWeight="bold" mb={1}>
                        Regime tributário pretendido
                      </FormLabel>
                      <Select placeholder="Selecione" {...field} value={field.value ?? ''}>
                        <option value="simples">Simples Nacional</option>
                        <option value="presumido">Lucro Presumido</option>
                        <option value="real">Lucro Real</option>
                      </Select>
                    </FormControl>
                  )}
                />
              </SimpleGrid>
              <Controller
                control={control}
                name="atividade_no_endereco"
                render={({ field }) => (
                  <Checkbox
                    mt={3}
                    isChecked={!!field.value}
                    onChange={e => field.onChange(e.target.checked)}
                  >
                    <Text fontSize="sm">
                      A atividade é exercida no endereço da sede? (afeta a viabilidade)
                    </Text>
                  </Checkbox>
                )}
              />
            </Box>
          )}

          {/* SCP (Fase 3) — painel de config da natureza scp (objeto/capital/
              prazo/cláusulas opcionais). Escreve em dados_scp.*. */}
          {isScp && <ScpConfigPanel />}
        </Box>
      )}
    </Box>
  );
};

export default StepEmpresa;
